import { useState, useCallback } from 'react';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { getContainerApiUrl } from '../utils/previewSession';
import { devLog } from '../utils/devLog';

/**
 * Dev-server port-mapping panel state + handlers, extracted out of
 * Canvas.jsx (DDD refactor Phase 2 — see /Users/giulioroggero/.claude/plans/
 * tender-roaming-bentley.md). Pure move, no external dependencies beyond the
 * shared fetch/auth utils Canvas.jsx already imports.
 */
export function usePortMappings() {
  const [portMappings, setPortMappings] = useState([]);
  const [portMappingsLoading, setPortMappingsLoading] = useState(false);
  const [portMappingsError, setPortMappingsError] = useState(null);
  const [killingPort, setKillingPort] = useState(null);

  // Function to fetch port mappings from container-live
  const fetchPortMappings = useCallback(async () => {
    try {
      setPortMappingsLoading(true);
      setPortMappingsError(null);

      // Fetch from React container (port 3001) as it manages all port mappings
      const apiUrl = getContainerApiUrl(3001, '/api/port-mappings');
      const response = await getAuthorizedFetch()(apiUrl, {
        headers: getBffHeaders(),
      });
      const data = await response.json();

      if (data.success) {
        setPortMappings(data.mappings || []);
      } else {
        setPortMappingsError(data.message || 'Failed to fetch port mappings');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch port mappings:', error);
      setPortMappingsError(error.message);
    } finally {
      setPortMappingsLoading(false);
    }
  }, []);

  // Function to kill process on a specific port
  const killProcessOnPort = useCallback(async (sessionId, port) => {
    try {
      setKillingPort(port);

      // Call kill API on React container (port 3001) to kill all processes and free the port
      const apiUrl = getContainerApiUrl(3001, `/api/preview/${sessionId}/kill`);
      const response = await getAuthorizedFetch()(apiUrl, {
        method: 'POST',
        headers: {
          ...getBffHeaders(),
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        // Show success notification
        devLog(`✓ All processes killed and port ${port} freed successfully`);
        // Refresh port mappings after a short delay
        setTimeout(() => {
          fetchPortMappings();
        }, 1000);
      } else {
        console.error(`Failed to kill process on port ${port}:`, data.message);
        alert(`Failed to kill process: ${data.message}`);
      }
    } catch (error) {
      console.error(`Error killing process on port ${port}:`, error);
      alert(`Error killing process: ${error.message}`);
    } finally {
      setKillingPort(null);
    }
  }, [fetchPortMappings]);

  return {
    portMappings,
    portMappingsLoading,
    portMappingsError,
    killingPort,
    fetchPortMappings,
    killProcessOnPort,
  };
}
